import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { round, sumBy } from 'lodash';
import { AllocationRule } from 'further-types/lp/allocations';
import { EqualisationGeneralCapitalCallsFormValues } from '../Editor/EqualiseGeneralCapitalCalls/FormWrapper';
import useAllocationsApi from 'hooks/lp/data/allocations/useAllocationsApi';
import { useLayoutContext } from 'contexts/LayoutContext';
import recalculateAndBalanceAllocations from '../Editor/EqualiseActions/helpers/recalculateAndBalanceAllocations';
import { useEqualisationSection } from '../Editor/EqualisationContext';

type UseEqualiseGeneralCapitalParams = {
  actionIndex: number;
  totalAmountLcy: number;
  originalInvestorBreakdown: Array<{
    commitmentId: string;
    calledFundsLcy: number;
  }>;
};

const useEqualiseGeneralCapital = ({
  actionIndex,
  totalAmountLcy,
  originalInvestorBreakdown,
}: UseEqualiseGeneralCapitalParams) => {
  const { selectedVehicleId } = useLayoutContext();
  const { commitments } = useEqualisationSection();
  const { setValue, watch } =
    useFormContext<EqualisationGeneralCapitalCallsFormValues>();
  const {
    generateAllocations,
    initialAllocationsData: newAllocationsData,
    isPending,
  } = useAllocationsApi();

  const newInvestorBreakdownPath =
    `generalCapitalCalls.${actionIndex}.newInvestorBreakdown` as const;
  const allocationRulePath =
    `generalCapitalCalls.${actionIndex}.allocationRule` as const;

  const watchedFields = watch(newInvestorBreakdownPath);

  const setToManualMode = () => {
    setValue(allocationRulePath, AllocationRule.Manual);
  };

  const updateCallOverpaidOrUnderpaid = (
    investorIndex: number,
    newCalledFundsLcy: number,
  ) => {
    const investor = watchedFields?.[investorIndex];
    if (!investor) return;

    const originalInvestor = originalInvestorBreakdown.find(
      (inv) => inv.commitmentId === investor.commitmentId,
    );
    const originalCalledFundsLcy = originalInvestor?.calledFundsLcy ?? 0;

    const newCallOverpaidOrUnderpaid = round(
      newCalledFundsLcy - originalCalledFundsLcy,
      2,
    );

    setValue(
      `${newInvestorBreakdownPath}.${investorIndex}.callOverpaidOrUnderpaid`,
      newCallOverpaidOrUnderpaid,
    );
  };

  const handleAllocationRuleChange = (value: AllocationRule) => {
    setValue(allocationRulePath, value);
    if (value === AllocationRule.ByCommitmentAndClosingDate) {
      generateAllocations({
        allocationRule: value,
        vehicleId: selectedVehicleId ?? '',
        amountTcy: totalAmountLcy,
        commitmentIdsToConsider: watchedFields.map((item) => item.commitmentId),
        excludedCommitmentIds: watchedFields
          .filter((item) => item.excludeFromAllocation)
          .map((item) => item.commitmentId),
        shouldIncludeCommitmentsBeingEqualised: true,
      });
    }
  };

  useEffect(() => {
    if (!isPending && newAllocationsData?.allocations) {
      newAllocationsData.allocations.forEach((allocation) => {
        const investorIndex = watchedFields?.findIndex(
          (field) => field.commitmentId === allocation.commitmentId,
        );
        if (investorIndex === undefined || investorIndex < 0) return;

        setValue(
          `${newInvestorBreakdownPath}.${investorIndex}.allocationPercentage`,
          allocation.allocationPercentage,
        );
        setValue(
          `${newInvestorBreakdownPath}.${investorIndex}.calledFundsLcy`,
          allocation.allocationAmountTcy,
        );
        setValue(
          `${newInvestorBreakdownPath}.${investorIndex}.excludeFromAllocation`,
          allocation.excludeFromAllocation,
        );

        updateCallOverpaidOrUnderpaid(
          investorIndex,
          allocation.allocationAmountTcy,
        );
      });
    }
  }, [
    newAllocationsData,
    isPending,
    newInvestorBreakdownPath,
    setValue,
    actionIndex,
    originalInvestorBreakdown,
  ]);

  const handleCalledFundsChange = (newValue: number, investorIndex: number) => {
    setValue(
      `${newInvestorBreakdownPath}.${investorIndex}.calledFundsLcy`,
      newValue,
    );

    const allocationPercentage =
      totalAmountLcy > 0 ? round((newValue / totalAmountLcy) * 100, 5) : 0;
    setValue(
      `${newInvestorBreakdownPath}.${investorIndex}.allocationPercentage`,
      allocationPercentage,
    );

    updateCallOverpaidOrUnderpaid(investorIndex, newValue);

    setToManualMode();
  };

  const handleAllocationPercentageChange = (
    newValue: number,
    investorIndex: number,
  ) => {
    setValue(
      `${newInvestorBreakdownPath}.${investorIndex}.allocationPercentage`,
      newValue,
    );

    const calledFundsLcy = round((newValue / 100) * totalAmountLcy, 2);
    setValue(
      `${newInvestorBreakdownPath}.${investorIndex}.calledFundsLcy`,
      calledFundsLcy,
    );

    const allocations =
      watchedFields.map((field) => ({
        commitmentId: field.commitmentId,
        allocationAmountLcy: field.calledFundsLcy,
        allocationPercentage: field.allocationPercentage,
        excludeFromAllocation: field.excludeFromAllocation,
      })) ?? [];

    const balancedAllocations = recalculateAndBalanceAllocations({
      allocations,
      commitments,
      targetAmountLcy: totalAmountLcy,
    });

    if (balancedAllocations) {
      balancedAllocations.forEach((balancedAllocation, index) => {
        setValue(
          `${newInvestorBreakdownPath}.${index}.calledFundsLcy`,
          balancedAllocation.allocationAmountLcy,
        );
      });
    }

    updateCallOverpaidOrUnderpaid(investorIndex, calledFundsLcy);

    setToManualMode();
  };

  const handleExcludeFromAllocationChange = (
    newValue: boolean,
    investorIndex: number,
  ) => {
    const investor = watchedFields?.[investorIndex];
    if (!investor) return;

    setValue(
      `${newInvestorBreakdownPath}.${investorIndex}.excludeFromAllocation`,
      newValue,
    );

    if (newValue) {
      setValue(
        `${newInvestorBreakdownPath}.${investorIndex}.callOverpaidOrUnderpaid`,
        0,
      );
      setValue(
        `${newInvestorBreakdownPath}.${investorIndex}.allocationPercentage`,
        0,
      );
      setValue(
        `${newInvestorBreakdownPath}.${investorIndex}.calledFundsLcy`,
        0,
      );
    } else {
      updateCallOverpaidOrUnderpaid(investorIndex, investor.calledFundsLcy);
    }

    const allocationRule = watch(allocationRulePath);
    if (allocationRule === AllocationRule.ByCommitmentAndClosingDate) {
      generateAllocations({
        allocationRule,
        vehicleId: selectedVehicleId ?? '',
        amountTcy: totalAmountLcy,
        commitmentIdsToConsider: watchedFields.map((item) => item.commitmentId),
        excludedCommitmentIds: watchedFields
          .filter((item) => item.excludeFromAllocation)
          .map((item) => item.commitmentId),
        shouldIncludeCommitmentsBeingEqualised: true,
      });
    }
  };

  const includedInvestors =
    watchedFields?.filter((inv) => !inv.excludeFromAllocation) || [];

  const totalCalledFunds = sumBy(includedInvestors, (inv) =>
    Number(inv.calledFundsLcy),
  );

  const totalAllocationPercentage = sumBy(includedInvestors, (inv) =>
    Number(inv.allocationPercentage),
  );

  const excludedInvestorNames = watchedFields
    ?.filter((inv) => inv.excludeFromAllocation)
    .map((inv) => inv.investorName)
    .join(', ');

  return {
    watchedFields,
    totalCalledFunds,
    totalAllocationPercentage,
    excludedInvestorNames,
    newInvestorBreakdownPath,
    isPending,
    onChangeAllocationRule: handleAllocationRuleChange,
    handleCalledFundsChange,
    handleAllocationPercentageChange,
    handleExcludeFromAllocationChange,
  };
};

export default useEqualiseGeneralCapital;
